// src/components/Navigation/MobileNavigation.tsx - Mobile navigation for Navbar
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserInitials } from '../../utils/userInitials';
import { isAdminDashboardArea } from '../../utils/adminDashboardTabs';
import { isInsurerDashboardArea } from '../../utils/insurerDashboardTabs';
export interface MobileNavigationProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  isActive: (path: string) => boolean;
  handleFeatureClick: (feature: string) => void;
  handleLogout: () => void;
  mobileMenuRef: React.RefObject<HTMLDivElement>;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isActive,
  handleFeatureClick,
  handleLogout,
  mobileMenuRef,
}) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const isAdmin = user && (user.is_superuser || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN');
  const isInsurer = user && user.role === 'INSURER';
  const isPartnerClient = Boolean(user?.insurer_org_id && user?.role === 'USER');
  const onInsurerDashboard = isInsurerDashboardArea(location.pathname);
  const onAdminArea = isAdminDashboardArea(location.pathname);
  const hideConsumerNav =
    isInsurer || isPartnerClient || (Boolean(isAdmin) && onAdminArea);

  return (
    <>
      {/* Hamburger button - always visible on mobile */}
      <div className="md:hidden">
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="inline-flex items-center justify-center p-2 text-gray-400 rounded-md hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
        >
          <span className="sr-only">Open main menu</span>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Mobile menu overlay - only visible when open */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={() => setIsMobileMenuOpen(false)} />

          {/* Menu panel */}
          <div className="fixed inset-y-0 left-0 w-full max-w-sm bg-white shadow-xl z-50 md:hidden overflow-y-auto transform transition-transform duration-300 ease-in-out" ref={mobileMenuRef}>
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-center justify-end mb-4">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex items-center justify-center p-2 text-gray-400 rounded-md hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                >
                  <span className="sr-only">Close main menu</span>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {isAuthenticated ? (
          <>
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="flex items-center px-4">
                <div className="flex justify-center items-center w-10 h-10 bg-gray-200 rounded-full">
                  <span className="text-sm font-medium text-gray-700">
                    {getUserInitials(user?.full_name, user?.email)}
                  </span>
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {user?.full_name || 'User'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {user?.email || 'user@example.com'}
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-2 pb-3 space-y-1">
              {isInsurer && !onInsurerDashboard && (
                <Link
                  to="/insurer-dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600"
                >
                  Insurer Dashboard
                </Link>
              )}

              {isAdmin && !isInsurer && !onAdminArea && (
                <>
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium ${
                      isActive('/admin') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    Admin
                  </Link>
                  <Link
                    to="/analytics-dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium ${
                      isActive('/analytics-dashboard') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    Analytics
                  </Link>
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium ${
                      isActive('/dashboard') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    Workspace
                  </Link>
                </>
              )}

              {/* Exercises and Continuity plan — only these are visible for normal users */}
              {!isInsurer && (!isAdmin || !onAdminArea) && (
                <>
                  <Link
                    to="/tabletop-exercises"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium ${
                      isActive('/tabletop-exercises') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    Exercises
                  </Link>
                  <Link
                    to="/bcp"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium ${
                      isActive('/bcp') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    Continuity plan
                  </Link>
                  <Link
                    to="/academy"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium ${
                      isActive('/academy') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    Academy
                  </Link>
                </>
              )}
            </div>

            <div className="pt-4 pb-3 border-t border-gray-200">
              <Link
                to="/profile"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-2 text-base font-medium ${
                  isActive('/profile') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Profile
              </Link>
              {!hideConsumerNav && (
              <>
              <Link
                to="/subscription-management"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-2 text-base font-medium ${
                  isActive('/subscription-management') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Subscriptions
              </Link>
              <Link
                to="/payment-history"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-2 text-base font-medium ${
                  isActive('/payment-history') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Payments
              </Link>
              <Link
                to="/billing-settings"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-2 text-base font-medium ${
                  isActive('/billing-settings') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                Billing
              </Link>
              </>
              )}

              {isAdmin && (
                <>
                  <div className="my-2 border-t border-gray-200"></div>
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-3 py-2 text-base font-medium ${
                      isActive('/admin') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    Admin
                  </Link>
                  {onAdminArea && (
                    <Link
                      to="/tabletop-exercises"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600"
                    >
                      Back to App
                    </Link>
                  )}
                </>
              )}

              <button
                onClick={handleLogout}
                className="block px-3 py-2 w-full text-base font-medium text-left text-red-600 hover:text-red-800"
              >
                Logout
              </button>
            </div>
          </>
        ) : (
          <div className="pt-2 pb-3 space-y-1">
            <Link
              to="/login"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-3 py-2 text-base font-medium rounded-md ${
                isActive('/login')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-700 hover:text-blue-600'
              }`}
            >
              Sign In
            </Link>
            <Link
              to="/register"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 text-base font-medium text-gray-700 rounded-md hover:text-blue-600"
            >
              Get Started
            </Link>
              </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MobileNavigation;
